import {Trans} from '@lingui/macro';
import {isEmpty} from 'lodash';
import React, {FC, ReactNode} from 'react';

import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {collectEnumValues} from '../../../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {
  DropdownBoxCoordinator,
  DropdownBoxCoordinatorProps,
} from '../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {styled} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DropdownCategory} from '../../../../../components/common/dropdowns/dropdownCategory';
import {DropdownItemIcon} from '../../../../../components/common/dropdowns/dropdownItemIcon';
import {RegexCharacterClassesEnum, RegexPart} from '../../../../editors/commons/stringTemplateModel';
import {ClientAtom} from '../../../../editors/expressions/expressionAtomModel';
import {getExpressionTypeDropdownIcon} from '../../../../editors/expressions/expressionIcons';
import {filterAllowedExpressions} from '../../../../editors/expressions/expressionInferHelpers';
import {previousResultLabel} from '../../../../editors/expressions/expressionLabels';
import {
  buildExpressionName,
  Expression,
  PREVIOUS_RESULT_ID,
} from '../../../../editors/expressions/expressionModel';
import {buildExpressionRef, ExpressionRef} from '../../../../editors/expressions/expressionRefModel';
import {GroupTitle, StyledDropdownGroupTitleContent} from '../../dropdowns/multipleGroupsDropdownGroup';
import {GenericSingleParameterProps} from '../../parameters/genericParameter';
import {RegexPartDropdownItem} from './regexPartDropdownItem';

/*
 * Style.
 */

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0;
`;

const StyledIcon = styled(Icon)`
  grid-area: right-icon;
  align-self: center;
`;

/*
 * Component.
 */

interface StringTemplateParameterProps extends Omit<GenericSingleParameterProps<Expression>, 'onChange'> {
  expressions: ReadonlyArray<Expression>;
  atom?: ClientAtom;
  onPartSelected: HandlerOf<ExpressionRef | RegexPart>;
  canUsePreviousResult?: boolean;
  canUseRegex?: boolean;
  renderButton?: (isOpen: boolean, props: DropdownBoxCoordinatorProps) => ReactNode;
  onSelectCreateExpression?: Handler;
}

export const StringTemplateParameter: FC<StringTemplateParameterProps> = (
  props: StringTemplateParameterProps,
) => (
  <StyledDropdownBoxCoordinator
    {...props}
    renderDropdown={(transferProps) => renderDropdown(transferProps, props)}
  />
);

function renderDropdown(transferProps: PopoverTransferProps, props: StringTemplateParameterProps) {
  return (
    <SimpleDropdown {...transferProps} width={300}>
      {maybeRenderCreateExpression(props)}
      {renderVariables(props)}
      {renderPlaceholders(props)}
    </SimpleDropdown>
  );
}

function maybeRenderCreateExpression({expressions, onSelectCreateExpression}: StringTemplateParameterProps) {
  if (!onSelectCreateExpression) {
    return null;
  }

  const title = (
    <GroupTitle key="group-create-new-expression" isNotInteractive>
      <StyledDropdownGroupTitleContent>
        <Trans id="create-expression-title">Create</Trans>
      </StyledDropdownGroupTitleContent>
    </GroupTitle>
  );

  return (
    <>
      {expressions.length > 0 ? title : null}
      <UserThemeConsumer>
        {(theme) => (
          <DropdownItem
            renderRightContent={() => (
              <StyledIcon name="chevronRightSmall" color={theme.palette.grey.shade30} />
            )}
            onSelect={onSelectCreateExpression}
          >
            <DropdownItemIcon
              iconSize={16}
              iconName="plusCircleFilledSmall"
              color={theme.palette.blue.shade40}
              label="Create"
            />
            <DropdownItemContent>
              <Trans id="create-expression-content">New dynamic variable</Trans>
            </DropdownItemContent>
          </DropdownItem>
        )}
      </UserThemeConsumer>
    </>
  );
}

function renderVariables(props: StringTemplateParameterProps) {
  const {atom, expressions, canUsePreviousResult, onPartSelected} = props;
  const allowedExpressions = filterAllowedExpressions(expressions, atom, false);

  if (isEmpty(allowedExpressions) && !canUsePreviousResult) {
    return null;
  }

  return (
    <>
      <DropdownCategory key="category-variables">
        <Trans id="rule-editor-variables">Dynamic variables</Trans>
      </DropdownCategory>
      {canUsePreviousResult && (
        <DropdownItem
          key={PREVIOUS_RESULT_ID}
          onSelect={() => onPartSelected({expression: PREVIOUS_RESULT_ID})}
        >
          <DropdownItemIcon label={null} iconName="variableSmall" />
          <DropdownItemContent>{previousResultLabel}</DropdownItemContent>
        </DropdownItem>
      )}
      {allowedExpressions.map((expression) => (
        <DropdownItem key={expression.id} onSelect={() => onPartSelected(buildExpressionRef(expression))}>
          <DropdownItemIcon label={null} iconName={getExpressionTypeDropdownIcon(expression.inferredType)} />
          <DropdownItemContent>{buildExpressionName(expression)}</DropdownItemContent>
        </DropdownItem>
      ))}
    </>
  );
}

function renderPlaceholders(props: StringTemplateParameterProps) {
  const {canUseRegex, onPartSelected} = props;

  if (!canUseRegex) {
    return null;
  }

  return (
    <>
      <DropdownCategory key="category-placeholders">
        <Trans id="rule-editor-placeholders">Placeholders</Trans>
      </DropdownCategory>
      {collectEnumValues(RegexCharacterClassesEnum).map((char) => (
        <RegexPartDropdownItem key={`regex-${char}`} part={{char}} onSelect={() => onPartSelected({char})} />
      ))}
    </>
  );
}
